import React, { useContext } from "react";
import { useFormik } from "formik";
import {
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Form,
  Row,
  Col,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import { ErrorToaster } from "components/common/toast";
import BtnCustom from "components/common/btn-custom";
import handleError from "utils/handleError";
import PATHS from "constant/paths";
import CustomInputField from "components/common/CustomInputField";
import CustomPasswordField from "components/common/CustomPasswordField";
import { CreateUsersAdminAPI } from "network/user";
import { UserContextProvider } from "context/UserContext";
import { CreateUsersResellerAPI } from "network/user";
import { roleOptions } from "constant/SelectOptions";
import { successToaster } from "components/common/toast";
import { GetResellerAPI } from "network/user";
import CustomPaginatedSelect from "components/common/CustomPaginatedSelect";
import { AddUserSchema } from "components/schema/AddUserSchema";
import ModulesPicker from "components/user/ModulesPicker";
import { useTranslation } from "react-i18next";

const AddUserFrom = () => {
  const { t } = useTranslation("users");
  const pageSize = 10;
  const navigate = useNavigate();
  const { user } = useContext(UserContextProvider);
  const isAdmin = ["admin", "super_admin"].includes(user?.role?.toLowerCase());

  const loadResellerOptions = async (
    search,
    prevOptions = [],
    additional = { page: 1 }
  ) => {
    const page = additional.page || 1;

    try {
      const response = await await GetResellerAPI(page, pageSize, search);

      if (response.success) {
        const newOptions = response?.data?.reseller?.map((item) => ({
          value: item?.uuid || "",
          label: item?.first_name + " " + item?.last_name,
        }));
        return {
          options: [...newOptions],
          hasMore: response?.data?.has_next,
          additional: { page: page + 1 },
        };
      }
    } catch (error) {
      console.error("Error fetching Reseller:", error);
    }

    return {
      options: prevOptions,
      hasMore: false,
      additional: { page: page + 1 },
    };
  };

  const {
    errors,
    values,
    handleBlur,
    handleChange,
    handleSubmit,
    touched,
    setFieldValue,
    isSubmitting,
    submitCount,
    setFieldTouched,
  } = useFormik({
    initialValues: {
      restaurantName: "",
      modules: [], // nessun modulo preselezionato: si spuntano quelli venduti
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      password: "",
      confirmPassword: "",

      role:
        user?.role?.toLowerCase() === "reseller"
          ? { value: "user", label: t("form.roleUser") }
          : "",
      reseller: "",
    },
    enableReinitialize: true,
    validationSchema: AddUserSchema,
    validate: (vals) => {
      const found = {};
      const isUser = (vals.role?.value || "").toLowerCase() === "user";
      if (isAdmin && !vals.restaurantName.trim()) {
        found.restaurantName = t("form.restaurantRequired");
      }
      if (isUser && vals.modules.length === 0) {
        found.modules = t("form.modulesRequired");
      }
      return found;
    },
    onSubmit: async (values, { resetForm }) => {
      try {
        const formData = new FormData();
        if (isAdmin) {
          formData.append("restaurant_name", values.restaurantName.trim());
        }
        if (values?.role?.value === "user") {
          values.modules.forEach((code) => formData.append("modules", code));
        }
        formData.append("first_name", values?.firstName);
        formData.append("last_name", values?.lastName);
        formData.append("email", values?.email);
        formData.append("phone_number", values?.phone);
        formData.append("role", values?.role?.value);
        formData.append("password", values?.password);
        formData.append("confirm_password", values?.confirmPassword);
        if (values?.reseller?.value) {
          formData.append("reseller", values?.reseller?.value);
        }
        let response;
        if (
          user?.role?.toLowerCase() === "admin" ||
          user?.role?.toLowerCase() === "super_admin"
        ) {
          response = await CreateUsersAdminAPI(formData);
        } else if (user?.role?.toLowerCase() === "reseller") {
          response = await CreateUsersResellerAPI(formData);
        } else {
          throw new Error("Invalid user role");
        }
        if (response?.success) {
          successToaster(t("form.added"));
          resetForm();
          navigate(PATHS.USER_LIST);
        }
      } catch (error) {
        console.error("Error:", error);
        ErrorToaster(handleError(error.status_code, error.message));
      }
    },
  });

  return (
    <Row>
      <Col md="7" className="mx-auto d-flex justify-content-center">
        <Card>
          <CardHeader>
            <h3 className="title text-capitalize">{t("form.addUserTitle")}</h3>
          </CardHeader>
          <CardBody>
            <Form onSubmit={handleSubmit}>
              {isAdmin && (
                <CustomInputField
                  label={t("form.restaurantName")}
                  name="restaurantName"
                  placeholder={t("form.restaurantName")}
                  type="text"
                  errors={errors}
                  touched={touched}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values}
                />
              )}
              <CustomInputField
                label={t("form.firstName")}
                name="firstName"
                placeholder={t("form.firstName")}
                type="text"
                errors={errors}
                touched={touched}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values}
              />
              <CustomInputField
                label={t("form.lastName")}
                name="lastName"
                placeholder={t("form.lastName")}
                type="text"
                errors={errors}
                touched={touched}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values}
              />
              <CustomInputField
                label={t("form.phone")}
                name="phone"
                placeholder={t("form.phonePlaceholder")}
                type="text"
                errors={errors}
                touched={touched}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values}
              />
              <CustomInputField
                label={t("form.email")}
                name="email"
                placeholder={t("form.email")}
                type="email"
                errors={errors}
                touched={touched}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values}
              />
              {(user?.role?.toLowerCase() === "admin" ||
                user?.role?.toLowerCase() === "super_admin") && (
                <CustomPaginatedSelect
                  label={t("form.selectRole")}
                  name="role"
                  placeholder={t("form.selectRole")}
                  staticOptions={roleOptions || []}
                  value={values.role}
                  onChange={(value) => setFieldValue("role", value)}
                  onBlur={() => setFieldTouched("role", true)}
                  touched={touched}
                  errors={errors}
                />
              )}

              {(user?.role?.toLowerCase() === "admin" ||
                user?.role?.toLowerCase() === "super_admin") && (
                <CustomPaginatedSelect
                  label={t("form.selectReseller")}
                  name="reseller"
                  placeholder={t("form.selectReseller")}
                  disabled={values?.role?.value?.toLowerCase() !== "user"}
                  value={values.reseller}
                  onChange={(value) => setFieldValue("reseller", value)}
                  onBlur={() => setFieldTouched("reseller", true)}
                  touched={touched}
                  errors={errors}
                  loadOptions={loadResellerOptions}
                />
              )}
              {values?.role?.value === "user" && (
                <ModulesPicker
                  value={values.modules}
                  onChange={(next) => setFieldValue("modules", next)}
                  error={submitCount > 0 ? errors.modules : ""}
                />
              )}
              <CustomPasswordField
                label={t("form.password")}
                name="password"
                placeholder={t("form.password")}
                errors={errors}
                touched={touched}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values}
              />
              <CustomPasswordField
                label={t("form.confirmPassword")}
                name="confirmPassword"
                placeholder={t("form.confirmPassword")}
                errors={errors}
                touched={touched}
                onChange={handleChange}
                onBlur={handleBlur}
                value={values}
              />

              <CardFooter className="text-center">
                <BtnCustom
                  className="btn-round"
                  color="primary"
                  size="lg"
                  type="submit"
                  isLoading={isSubmitting}
                  submittingText={t("form.adding")}
                >
                  {t("form.addUserButton")}
                </BtnCustom>
              </CardFooter>
            </Form>
          </CardBody>
        </Card>
      </Col>
    </Row>
  );
};

export default AddUserFrom;
